// © 2015-2021 WDNA - All Rights Reserved

import moment from 'moment';
import * as React from 'react';

import DatePicker, { DateSelectorConfig } from 'wdna-date-selector';

import { GridApi, RowDataUpdatedEvent, ICellRendererParams } from 'wdna-grid-table/lib/types';
import Table from 'wdna-components/lib/table/Table';
import ContainerModal from 'wdna-components/lib/modals/generics/ContainerModal';
import { messageError } from 'wdna-components/lib/helpers/notification';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';

import { weather } from '../../../../sensors/requests';

interface Props {
  id: number | undefined;
  show: boolean;
  onClose: () => void;
}

interface State {
  flatpickrMode: string;
  groupDateTime: string;

  dates: Date[];
  dateIni: string;
  timeIni: string;
  dateEnd: string;
  timeEnd: string;

  data: any[];
}

export default class Report extends React.Component<Props, State> {
  private pid: string;
  private mounted: boolean;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;

    const dIni: Date = new Date();
    dIni.setDate(dIni.getDate() - 2);
    const dEnd: Date = new Date();

    this.state = {
      dates: this.initialDates(),
      flatpickrMode: 'range',
      groupDateTime: '24h',

      dateIni: moment(dIni).add(1, 'days').format('YYYY-MM-DD'),
      timeIni: '00:00',
      dateEnd: moment(dEnd).add(1, 'days').format('YYYY-MM-DD'),
      timeEnd: '00:00',

      data: []
    };
  }

  public componentDidMount(): void {
    this.mounted = true;
  }

  public componentWillUnmount(): void {
    this.mounted = false;
  }

  public setLocalState = (nextState: Partial<State>, callback?: (() => void)): void => {
    if (this.mounted) {
      this.setState((prevState: State) => ({
        ...prevState,
        ...nextState,
      }), callback);
    }
  };

  public initialDates = (): Date[] => {
    const today: Date = new Date();
    const twoDaysBefore: Date = moment().add(-2, 'days').toDate();
    return [twoDaysBefore, today];
  };

  public submit = (): void => {
    const { dateIni, timeIni, dateEnd, timeEnd } = this.state;
    const idSensor: number | undefined = this.props.id;
    const req1: Record<string, any> = {};

    if (dateIni) req1.from = moment(`${dateIni}T${timeIni || '00'}:00`, 'YYYY-MM-DDThh:mm').toDate().toISOString();
    if (dateEnd) req1.to = moment(`${dateEnd}T${timeEnd || '00'}:00`, 'YYYY-MM-DDThh:mm').toDate().toISOString();

    req1.values = [0, 1, 2, 2, 2, 4, 3, 6, 5, 8];
    req1.operators = ['amean', 'mean', 'mean', 'max', 'min', 'sum', 'mean', 'mean', 'mean', 'max'];
    req1.group = 1440;
    req1.round = false;

    this.loadingStart();
    weather('statistics', String(idSensor), req1)
      ?.then((resp: any[]) => {
        resp.splice(0, 1);
        this.setLocalState({ data: resp });
      })
      .catch((error: any) => {
        messageError('Error', error.message);
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public loadingStart = (): void => {
    window.store.dispatch(initProcess(this.pid));
  };

  public loadingFinish = (): void => {
    window.store.dispatch(finishProcess(this.pid));
  };

  public handleChangeDatePicker = (config: DateSelectorConfig): void => {
    const { flatpickrMode } = this.state;

    const hours: string[] = config.getHours() as string[];
    const dates: Date[] = config.getDates() as Date[];
    const newDataIni: moment.Moment = moment(dates[0]);
    const newDataEnd: moment.Moment = (flatpickrMode === 'range') ? moment(dates[1]) : moment(newDataIni).add(1, 'days');

    this.setLocalState({
      dates,
      timeIni: hours[0],
      timeEnd: hours[1],
      dateIni: newDataIni.format('YYYY-MM-DD'),
      dateEnd: newDataEnd.format('YYYY-MM-DD'),
      flatpickrMode: config.getMode(),
    });
  };

  public dateCellRenderer = (params: ICellRendererParams): string => {
    let { value } = params;
    [value] = value.split(' ');
    return value;
  };

  public numberCellRenderer = (params: ICellRendererParams): string => {
    let { value } = params;
    value = !isNaN(value) ? value && value.toFixed(2) : '';
    return value;
  };

  public numberCellRenderer2 = (params: ICellRendererParams): string => {
    let { value } = params;
    value = !isNaN(value) ? value && (value * 1000).toFixed(2) : '';
    return value;
  };

  public rowDataUpdated = (event: RowDataUpdatedEvent): void => {
    const gridApi: GridApi = event.api;
    setTimeout((): void => {
      gridApi?.sizeColumnsToFit();
    }, 100);
  };

  public render(): JSX.Element {
    const { show, onClose } = this.props;
    const { dates, data, flatpickrMode, groupDateTime } = this.state;

    const columnAlias2: Record<number, string> = {
      0: 'Date',
      1: 'Count',
      2: 'W-Speed Mean (km/h)',
      5: 'T (ºC)',
      6: 'T Min (ºC)',
      7: 'T Max (ºC)',
      8: 'HR (%)',
      11: 'P (hPa)',
      17: 'R_s (W/m2)',
      25: 'W-Gust (km/h)',
      29: 'Pr Sum (l/m2)',
      30: 'W-Dir Mean (º)',
      31: 'ET (mm)',
    };
    const columnsToHide2: string[] = [
      '1', '3', '4', '9', '10', '12', '13', '14', '15',
      '16', '18', '19', '20', '21', '22', '23', '24', '26', '27', '28'
    ];
    const columnStyles2: Record<number, any> = {
      0: { textAlign: 'center' },
    };
    const customColumnsDefs2: Record<number, any> = {
      0: { cellRenderer: this.dateCellRenderer },
      2: { cellRenderer: this.numberCellRenderer },
      5: { cellRenderer: this.numberCellRenderer },
      6: { cellRenderer: this.numberCellRenderer },
      7: { cellRenderer: this.numberCellRenderer },
      8: { cellRenderer: this.numberCellRenderer },
      11: { cellRenderer: this.numberCellRenderer2 },
      17: { cellRenderer: this.numberCellRenderer },
      25: { cellRenderer: this.numberCellRenderer },
      29: { cellRenderer: this.numberCellRenderer },
      30: { cellRenderer: this.numberCellRenderer },
      31: { cellRenderer: this.numberCellRenderer },
    };

    return (
      <ContainerModal
        fullScreenButton={false}
        show={show}
        title="History Report"
        modalClass="selector-modal-70"
        onClose={onClose}
        footerButtons={[
          {
            text: 'OK',
            className: 'btn btn-primary',
            onClick: onClose,
          },
        ]}
      >
        <div className="row">
          <div className="col-md-6">
            <DatePicker
              icon
              hours={[]}
              dates={dates}
              showChangeModes
              mode={flatpickrMode}
              granularity={groupDateTime}
              iconClass="wdna-ico-icon-calendar"
              handleChanges={this.handleChangeDatePicker}
            />
          </div>
          <div className="col-md-6">
            <button type="button" className="filter-btn btn btn-default" onClick={this.submit}>
              <i className="wdna-ico-icon-playsearch" />
              <span>GO</span>
            </button>
          </div>
        </div>
        <br />
        <header>
          <span className="widget-icon" style={{ padding: '8px' }}>
            <i className="fa fa-edit" />
          </span>
          Daily Summary Data
        </header>
        <Table
          data={data || []}
          alias={columnAlias2}
          customCellStyles={columnStyles2}
          hiddenCols={columnsToHide2}
          rowDataUpdated={this.rowDataUpdated}
          customColumnsDefs={customColumnsDefs2}
          height="calc(50vh - 7px)"
          pagination
        />
      </ContainerModal>
    );
  }
}
